import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import {
  formatPermissionLabel,
  getEffectivePermissions,
  getHighRiskPermissionKeys,
  getPermissionGroups,
  getPermissionRule,
  getRequiredPermissionKeys,
  getRoleDefaultPermissions,
  normalizePermissionOverrides,
  validatePermissionDependencies,
} from '../../../utils/permissions';


function detectActivePreset(module, overrides, grantable, roleDefaults) {
  const keys = module.actions.map((a) => `${module.key}.${a}`);
  if (keys.length === 0) return null;

  // inherit: none of the keys have an override
  const hasAnyOverride = keys.some((k) => overrides[k] !== undefined);
  if (!hasAnyOverride) return 'inherit';

  // deny: all keys are 'deny'
  const allDeny = keys.every((k) => overrides[k] === 'deny');
  if (allDeny) return 'deny';

  // view_only: view key is allowed (or inherited when role has it), all non-view keys are deny
  const viewKey = `${module.key}.view`;
  const nonViewKeys = keys.filter((k) => k !== viewKey);
  const viewIsAllow = overrides[viewKey] === 'allow' || (overrides[viewKey] === undefined && roleDefaults.has(viewKey));
  const nonViewAllDeny = nonViewKeys.length === 0 || nonViewKeys.every((k) => overrides[k] === 'deny');
  if (viewIsAllow && nonViewAllDeny && module.actions.includes('view')) return 'view_only';

  // allow_all: all grantable keys are 'allow'
  const grantableKeys = keys.filter((k) => grantable.has(k));
  if (grantableKeys.length > 0 && grantableKeys.every((k) => overrides[k] === 'allow')) return 'allow_all';

  return null;
}

function cloneOverrides(overrides) {
  return { ...(overrides || {}) };
}

function applyModulePreset(module, preset, currentOverrides, grantablePermissions, roleDefaults) {
  const next = cloneOverrides(currentOverrides);

  module.actions.forEach((action) => {
    const key = `${module.key}.${action}`;
    const isGrantable = grantablePermissions.has(key);
    const isRoleDefault = roleDefaults.has(key);

    if (preset === 'inherit') {
      delete next[key];
      return;
    }

    if (preset === 'deny') {
      next[key] = 'deny';
      return;
    }

    if (preset === 'view_only') {
      if (action === 'view') {
        if (isGrantable) {
          next[key] = 'allow';
        } else if (isRoleDefault) {
          delete next[key];
        }
      } else {
        next[key] = 'deny';
      }
      return;
    }

    if (preset === 'allow_all' && isGrantable) {
      next[key] = 'allow';
    }
  });

  return next;
}

function buildGroupMap(registry) {
  const groups = new Map((getPermissionGroups(registry) || []).map((group) => [group.key, { ...group, modules: [] }]));
  registry.modules.forEach((module) => {
    const groupKey = module.group || 'other';
    if (!groups.has(groupKey)) {
      groups.set(groupKey, { key: groupKey, label: groupKey, modules: [] });
    }
    groups.get(groupKey).modules.push(module);
  });
  return Array.from(groups.values()).filter((group) => group.modules.length > 0);
}

function RoleDefaultSummary({ roleDefaults, module }) {
  const { t } = useTranslation();
  const labels = module.actions
    .map((action) => `${module.key}.${action}`)
    .filter((key) => roleDefaults.has(key))
    .map((key) => formatPermissionLabel(key))
    .slice(0, 4);

  if (labels.length === 0) {
    return <span>{t('permissionMatrix.noDefaultAccess')}</span>;
  }

  return <span>{labels.join(', ')}{module.actions.length > 4 && labels.length === 4 ? '...' : ''}</span>;
}

export default function PermissionMatrix({
  registry,
  grantablePermissions,
  role,
  value,
  onChange,
  disabled,
}) {
  const { t } = useTranslation();
  const overrides = normalizePermissionOverrides(value, registry);
  const grantable = grantablePermissions || new Set();
  const roleDefaults = useMemo(() => getRoleDefaultPermissions(role, registry), [role, registry]);
  const effectivePermissions = useMemo(
    () => getEffectivePermissions({ role, permission_overrides: overrides }, registry),
    [role, overrides, registry]
  );
  const dependencyIssues = useMemo(
    () => validatePermissionDependencies(role, overrides, registry),
    [role, overrides, registry]
  );
  const highRiskKeys = useMemo(
    () => getHighRiskPermissionKeys(role, overrides, registry),
    [role, overrides, registry]
  );
  const groupedModules = useMemo(() => buildGroupMap(registry), [registry]);
  const allowCount = Object.values(overrides).filter((value) => value === 'allow').length;
  const denyCount = Object.values(overrides).filter((value) => value === 'deny').length;

  return (
    <div className="mt-3 permission-matrix-container">
      <div className="border rounded p-3 mb-3 bg-light">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h6 className="mb-1">{t('permissionMatrix.heading')}</h6>
            <p className="text-muted small mb-0">
              {t('permissionMatrix.description')}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Badge bg="secondary">{t('permissionMatrix.badges.effectivePermissions', { count: effectivePermissions.size })}</Badge>
            <Badge bg="success">{t('permissionMatrix.badges.explicitAllows', { count: allowCount })}</Badge>
            <Badge bg="dark">{t('permissionMatrix.badges.explicitBlocks', { count: denyCount })}</Badge>
            {highRiskKeys.length > 0 && <Badge bg="danger">{t('permissionMatrix.badges.highRisk', { count: highRiskKeys.length })}</Badge>}
          </div>
        </div>
      </div>

      {dependencyIssues.length > 0 && (
        <Alert variant="warning">
          <strong>{t('permissionMatrix.dependencyWarning.title')}</strong> {t('permissionMatrix.dependencyWarning.description')}
          <ul className="mb-0 mt-2 ps-3">
            {dependencyIssues.map((issue) => (
              <li key={issue.permissionKey}>
                {t('permissionMatrix.dependencyWarning.requires', {
                  permission: formatPermissionLabel(issue.permissionKey, registry),
                  missing: issue.missing.map((permissionKey) => formatPermissionLabel(permissionKey, registry)).join(', '),
                })}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {highRiskKeys.length > 0 && (
        <Alert variant="danger">
          <strong>{t('permissionMatrix.highRiskWarning.title')}</strong>{' '}
          {highRiskKeys
            .slice(0, 4)
            .map((permissionKey) => formatPermissionLabel(permissionKey, registry))
            .join(', ')}
          {highRiskKeys.length > 4 ? ` ${t('permissionMatrix.highRiskWarning.andMore', { count: highRiskKeys.length - 4 })}` : '.'}
        </Alert>
      )}

      <Accordion alwaysOpen defaultActiveKey={groupedModules[0]?.key ? [groupedModules[0].key] : []}>
        {groupedModules.map((group) => (
          <Accordion.Item eventKey={group.key} key={group.key}>
            <Accordion.Header>{group.label}</Accordion.Header>
            <Accordion.Body className="d-grid gap-3">
              {group.modules.map((module) => {
                const activePreset = detectActivePreset(module, overrides, grantable, roleDefaults);
                const presetBtn = (preset, label) => {
                  const isActive = activePreset === preset;
                  return (
                    <Button
                      key={preset}
                      size="sm"
                      variant="light"
                      disabled={disabled}
                      className={`perm-preset-btn${isActive ? ' perm-active' : ''}`}
                      onClick={() => onChange(applyModulePreset(module, preset, overrides, grantable, roleDefaults))}
                    >
                      {label}
                    </Button>
                  );
                };
                return (
                <Card key={module.key} className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                      <div>
                        <h6 className="mb-1">{module.label}</h6>
                        <p className="text-muted small mb-1">{module.summary}</p>
                        <div className="small text-muted">
                          {t('permissionMatrix.roleDefault')} <RoleDefaultSummary roleDefaults={roleDefaults} module={module} />
                        </div>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {presetBtn('inherit',  t('permissionMatrix.presets.useRoleDefault'))}
                        {presetBtn('deny',     t('permissionMatrix.presets.blockModule'))}
                        {presetBtn('view_only',t('permissionMatrix.presets.viewOnly'))}
                        {presetBtn('allow_all',t('permissionMatrix.presets.allowAll'))}
                      </div>
                    </div>

                    <Table responsive className="mb-0 align-middle" size="sm">
                      <thead>
                        <tr>
                          <th style={{ minWidth: 220 }}>{t('permissionMatrix.columns.permission')}</th>
                          <th>{t('permissionMatrix.columns.description')}</th>
                          <th style={{ minWidth: 160 }}>{t('permissionMatrix.columns.state')}</th>
                          <th style={{ minWidth: 170 }}>{t('permissionMatrix.columns.effective')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {module.actions.map((action) => {
                          const key = `${module.key}.${action}`;
                          const rule = getPermissionRule(key, registry);
                          const isGrantable = grantable.has(key);
                          const overrideValue = overrides[key] || 'inherit';
                          const isEffective = effectivePermissions.has(key);
                          const roleHasPermission = roleDefaults.has(key);
                          const requiredBy = getRequiredPermissionKeys(key, registry)
                            .map((requiredKey) => formatPermissionLabel(requiredKey, registry))
                            .join(', ');

                          return (
                            <tr key={key}>
                              <td>
                                <div className="fw-semibold">{formatPermissionLabel(key, registry)}</div>
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                  <Badge bg={rule.risk === 'critical' ? 'danger' : rule.risk === 'high' ? 'warning' : 'secondary'}>
                                    {rule.risk || 'standard'}
                                  </Badge>
                                  {!isGrantable && <Badge bg="light" text="dark">{t('permissionMatrix.cannotAllow')}</Badge>}
                                </div>
                              </td>
                              <td>
                                <div>{rule.description || t('permissionMatrix.noDescription')}</div>
                                {requiredBy && (
                                  <div className="text-muted small mt-1">{t('permissionMatrix.requires')} {requiredBy}</div>
                                )}
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  disabled={disabled}
                                  value={overrideValue}
                                  onChange={(event) => {
                                    const next = cloneOverrides(overrides);
                                    if (event.target.value === 'inherit') {
                                      delete next[key];
                                    } else {
                                      next[key] = event.target.value;
                                    }
                                    onChange(next);
                                  }}
                                >
                                  <option value="inherit">{t('permissionMatrix.options.useRoleDefault')}</option>
                                  <option value="allow" disabled={!isGrantable}>{t('permissionMatrix.options.allowExplicitly')}</option>
                                  <option value="deny">{t('permissionMatrix.options.blockExplicitly')}</option>
                                </Form.Select>
                              </td>
                              <td>
                                <div className="fw-semibold">{isEffective ? t('permissionMatrix.state.allowed') : t('permissionMatrix.state.blocked')}</div>
                                <div className="text-muted small">
                                  {overrideValue === 'allow'
                                    ? t('permissionMatrix.state.explicitAllow')
                                    : overrideValue === 'deny'
                                      ? t('permissionMatrix.state.explicitBlock')
                                      : roleHasPermission
                                        ? t('permissionMatrix.state.inheritedFromRole')
                                        : t('permissionMatrix.state.notGrantedByRole')}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
                );
              })}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
}
